import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { ListChecks, Download, Calendar, UserRound } from "lucide-react";
import { api, API, downloadCsvViaApi } from "../../api";
import { useAuth } from "../../context/AuthContext";

// ---------- date helpers ----------
const iso = (d) => d.toISOString().slice(0, 10);
const startOfWeek = (d = new Date()) => {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0);
  return x;
};
const endOfWeek = (d = new Date()) => { const x = startOfWeek(d); x.setDate(x.getDate() + 6); return x; };
const startOfMonth = (d = new Date()) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d = new Date()) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const startOfQuarter = (d = new Date()) => { const q = Math.floor(d.getMonth() / 3); return new Date(d.getFullYear(), q * 3, 1); };
const endOfQuarter = (d = new Date()) => { const q = Math.floor(d.getMonth() / 3); return new Date(d.getFullYear(), q * 3 + 3, 0); };
const startOfYear = (d = new Date()) => new Date(d.getFullYear(), 0, 1);
const endOfYear = (d = new Date()) => new Date(d.getFullYear(), 11, 31);

const PRESETS = [
  { key: "week", label: "This week" },
  { key: "month", label: "This month" },
  { key: "quarter", label: "This quarter" },
  { key: "year", label: "This year" },
  { key: "all", label: "All time" },
  { key: "custom", label: "Custom" },
];

function rangeFor(preset, custom) {
  const today = new Date();
  switch (preset) {
    case "week": return { from: iso(startOfWeek(today)), to: iso(endOfWeek(today)), bucket: "day" };
    case "month": return { from: iso(startOfMonth(today)), to: iso(endOfMonth(today)), bucket: "day" };
    case "quarter": return { from: iso(startOfQuarter(today)), to: iso(endOfQuarter(today)), bucket: "week" };
    case "year": return { from: iso(startOfYear(today)), to: iso(endOfYear(today)), bucket: "month" };
    case "all": return { from: null, to: null, bucket: "month" };
    case "custom":
    default: return { from: custom?.from || null, to: custom?.to || null, bucket: custom?.bucket || "day" };
  }
}

function labelForBucket(k, bucket) {
  if (bucket === "day") {
    const d = new Date(`${k}T00:00:00`);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
  if (bucket === "week") {
    const d = new Date(`${k}T00:00:00`);
    return `wk ${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
  }
  // month "YYYY-MM"
  const [y, m] = k.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: "short", year: "2-digit" });
}

// ---------- CSV helpers ----------
function toCsv(rows, columns) {
  const escape = (v) => {
    if (v == null) return "";
    const s = String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const head = columns.map((c) => escape(c.label)).join(",");
  const body = rows.map((r) => columns.map((c) => escape(typeof c.get === "function" ? c.get(r) : r[c.key])).join(",")).join("\n");
  return `${head}\n${body}\n`;
}
function downloadCsv(name, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// ---------- component ----------
export default function Reports({ mine = false }) {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [timeseries, setTimeseries] = useState({ bucket: "day", series: [] });
  const [preset, setPreset] = useState("month");
  const [custom, setCustom] = useState({ from: iso(startOfMonth()), to: iso(endOfMonth()), bucket: "day" });
  const [tab, setTab] = useState("project");
  const [loading, setLoading] = useState(true);

  const range = useMemo(() => rangeFor(preset, custom), [preset, custom]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const params = {};
      if (range.from) params.date_from = range.from;
      if (range.to) params.date_to = range.to;
      if (mine) params.user_id = "me";
      const tsParams = { ...params, bucket: range.bucket };
      try {
        const [p, c, s, ts, ent] = await Promise.all([
          api.projects(), api.clients(),
          api.reportSummary(params), api.reportTimeseries(tsParams),
          api.entries({
            ...(range.from ? { date_from: range.from, date_to: range.to } : {}),
            ...(mine ? { user_id: "me" } : {}),
          }),
        ]);
        setProjects(p); setClients(c);
        setSummary(s); setTimeseries(ts); setEntries(ent);
      } finally { setLoading(false); }
    })();
  }, [range.from, range.to, range.bucket, mine]);

  const projById = Object.fromEntries(projects.map((p) => [p.id, p]));
  const cliById = Object.fromEntries(clients.map((c) => [c.id, c]));

  const byProject = (summary?.by_project || []).map((r) => {
    const p = projById[r.project_id];
    return { ...r, name: p?.name || "Unknown", client: p ? cliById[p.client_id] : null };
  }).sort((a, b) => b.hours - a.hours);
  const maxHours = Math.max(...byProject.map((x) => x.hours), 1);
  const byTask = (summary?.by_task || []).slice().sort((a, b) => b.hours - a.hours);
  const maxTaskHours = Math.max(...byTask.map((x) => x.hours), 1);

  const byClient = clients.map((c) => {
    const projs = projects.filter((p) => p.client_id === c.id);
    const rows = projs.map((p) => byProject.find((x) => x.project_id === p.id) || { hours: 0, revenue: 0 });
    return {
      ...c,
      hours: rows.reduce((a, r) => a + r.hours, 0),
      revenue: rows.reduce((a, r) => a + r.revenue, 0),
    };
  });

  const total = summary?.total_hours ?? 0;
  const billable = summary?.billable_hours ?? 0;
  const nonBillable = summary?.non_billable_hours ?? 0;
  const revenue = summary?.revenue ?? 0;
  const cost = summary?.cost ?? 0;
  const profit = summary?.profit ?? 0;

  const chartData = timeseries.series.map((s) => ({ ...s, label: labelForBucket(s.bucket, timeseries.bucket) }));

  const periodLabel = range.from
    ? `${range.from} – ${range.to || "today"}`
    : "All time";

  // ---------- CSV exports ----------
  const exportEntries = () => {
    downloadCsv(`fullbench-entries-${range.from || "all"}-${range.to || ""}.csv`, toCsv(entries, [
      { label: "Date", get: (e) => e.date },
      { label: "Project", get: (e) => projById[e.project_id]?.name || "" },
      { label: "Client", get: (e) => cliById[projById[e.project_id]?.client_id]?.name || "" },
      { label: "Task", get: (e) => e.task },
      { label: "Notes", get: (e) => e.notes || "" },
      { label: "Hours", get: (e) => e.hours },
      { label: "Billable", get: (e) => (e.billable ? "yes" : "no") },
    ]));
  };
  const exportByProject = () => downloadCsv(`fullbench-by-project-${range.from || "all"}.csv`, toCsv(byProject, [
    { label: "Project", get: (r) => r.name },
    { label: "Client", get: (r) => r.client?.name || "" },
    { label: "Hours", key: "hours" },
    { label: "Revenue", key: "revenue" },
    { label: "Cost", key: "cost" },
    { label: "Margin", get: (r) => (r.revenue - r.cost).toFixed(2) },
  ]));
  const exportByTask = () => downloadCsv(`fullbench-by-task-${range.from || "all"}.csv`, toCsv(byTask, [
    { label: "Task", get: (r) => r.task_name },
    { label: "Hours", key: "hours" },
    { label: "Billable hours", key: "billable_hours" },
    { label: "Revenue", key: "revenue" },
    { label: "Cost", key: "cost" },
    { label: "Margin", get: (r) => (r.revenue - r.cost).toFixed(2) },
  ]));
  const exportByClient = () => downloadCsv(`fullbench-by-client-${range.from || "all"}.csv`, toCsv(byClient, [
    { label: "Client", get: (r) => r.name },
    { label: "Hours", key: "hours" },
    { label: "Revenue", key: "revenue" },
  ]));

  return (
    <div data-testid={mine ? "my-reports-page" : "reports-page"}>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-sm text-[#6a736f] inline-flex items-center gap-1">
            {mine ? <><UserRound size={12} /> Personal report</> : "Reports"}
          </div>
          <h1 className="font-display text-4xl mt-1">
            {mine ? `${user?.name || "My"} · Time report` : "Performance"}
          </h1>
          <div className="text-xs text-[#6a736f] mt-1" data-testid="reports-period-label">
            {periodLabel}
            {loading && <span className="ml-2 text-coral">Loading…</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {mine && (
            <button
              onClick={() => downloadCsvViaApi(
                "/reports/export.csv",
                {
                  user_id: "me",
                  ...(range.from ? { date_from: range.from } : {}),
                  ...(range.to ? { date_to: range.to } : {}),
                },
                // undefined → honour server Content-Disposition (time_report_<user>_<range>.csv)
              )}
              data-testid="export-my-time-report"
              className="btn-outline-forest inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium"
            >
              <Download size={14} /> Time report CSV
            </button>
          )}
          <button
            onClick={exportEntries}
            data-testid="export-entries-csv"
            className="btn-coral inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="mt-4 bg-white rounded-xl border border-cream p-3 flex items-center gap-2 flex-wrap" data-testid="reports-filter-bar">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPreset(p.key)}
            data-testid={`preset-${p.key}`}
            className={`text-xs px-3 py-1.5 rounded-md border ${preset === p.key ? "bg-forest text-white border-forest" : "border-cream text-forest hover:bg-cream-2"}`}
          >
            {p.label}
          </button>
        ))}
        {preset === "custom" && (
          <div className="flex items-center gap-2 ml-2 flex-wrap">
            <Calendar size={14} className="text-[#6a736f]" />
            <input
              type="date" value={custom.from || ""}
              onChange={(e) => setCustom({ ...custom, from: e.target.value })}
              data-testid="custom-from"
              className="border border-cream rounded-md px-2 py-1 text-xs"
            />
            <span className="text-xs text-[#6a736f]">to</span>
            <input
              type="date" value={custom.to || ""}
              onChange={(e) => setCustom({ ...custom, to: e.target.value })}
              data-testid="custom-to"
              className="border border-cream rounded-md px-2 py-1 text-xs"
            />
            <select
              value={custom.bucket}
              onChange={(e) => setCustom({ ...custom, bucket: e.target.value })}
              data-testid="custom-bucket"
              className="border border-cream rounded-md px-2 py-1 text-xs bg-white"
            >
              <option value="day">Group by day</option>
              <option value="week">Group by week</option>
              <option value="month">Group by month</option>
            </select>
          </div>
        )}
      </div>

      {/* KPI row */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4">
        <Kpi label="Total tracked" value={`${total.toFixed(2)}h`} />
        <Kpi label="Billable" value={`${billable.toFixed(2)}h`} accent />
        <Kpi label="Non-billable" value={`${nonBillable.toFixed(2)}h`} />
        <Kpi label="Revenue" value={`$${revenue.toLocaleString()}`} />
        <Kpi label="Est. cost" value={`$${cost.toLocaleString()}`} sub={`Profit $${profit.toLocaleString()}`} />
      </div>

      {/* Trend chart */}
      <div className="mt-6 bg-white rounded-xl border border-cream p-5" data-testid="trend-chart-card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="font-semibold">Hours over time</div>
            <div className="text-xs text-[#6a736f]">Bucketed by {timeseries.bucket}</div>
          </div>
        </div>
        <div className="mt-4" style={{ width: "100%", height: 260 }} data-testid="trend-chart">
          {chartData.length === 0 ? (
            <div className="text-sm text-[#6a736f] text-center py-16">No tracked time in this period.</div>
          ) : (
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EAF3EE" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6a736f" }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11, fill: "#6a736f" }} unit="h" />
                <Tooltip
                  formatter={(v, name) => [`${Number(v).toFixed(2)}h`, name === "billable" ? "Billable" : "Non-billable"]}
                  contentStyle={{ border: "1px solid #EAF3EE", borderRadius: 8, fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="billable" stackId="a" fill="#EE7A57" name="Billable" />
                <Bar dataKey="non_billable" stackId="a" fill="#143A25" name="Non-billable" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* By-task summary tile */}
      <div className="mt-6 bg-white rounded-xl border border-cream p-5" data-testid="by-task-tile">
        <div className="flex items-center gap-2">
          <ListChecks size={18} className="text-coral" />
          <div className="font-semibold">Top tasks by hours</div>
        </div>
        {byTask.length === 0 ? (
          <p className="text-sm text-[#6a736f] mt-4">No task-tagged time in this period. Attach tasks to projects to start segmenting time and cost.</p>
        ) : (
          <div className="mt-4 space-y-3">
            {byTask.slice(0, 5).map((t) => {
              const key = t.task_id || t.task_name;
              const pct = (t.hours / maxTaskHours) * 100;
              return (
                <div key={key} data-testid={`by-task-row-${key}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="truncate max-w-[60%]">
                      {t.task_name}
                      {!t.task_id && <span className="ml-2 text-[10px] bg-cream text-[#6a736f] px-1 rounded">unclassified</span>}
                    </span>
                    <div className="tabular-nums text-[#6a736f]">
                      {t.hours.toFixed(2)}h · ${t.revenue.toLocaleString()} rev
                      {t.cost > 0 && <> · <span className="text-coral">${t.cost.toLocaleString()} cost</span></>}
                    </div>
                  </div>
                  <div className="mt-1.5 h-2 bg-[#EAF3EE] rounded-full overflow-hidden">
                    <div className="h-full bg-forest" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Tabbed drill-down */}
      <div className="mt-8 border-b border-cream flex items-center gap-4 text-sm">
        {[
          { k: "project", label: "By Project", onExport: exportByProject },
          { k: "task", label: "By Task", onExport: exportByTask },
          { k: "client", label: "By Client", onExport: exportByClient },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`reports-tab-${t.k}`}
            className={`pb-2 -mb-px border-b-2 ${tab === t.k ? "border-coral text-coral font-semibold" : "border-transparent text-[#6a736f] hover:text-forest"}`}
          >
            {t.label}
          </button>
        ))}
        <div className="ml-auto pb-2">
          <button
            onClick={
              tab === "project" ? exportByProject :
              tab === "task" ? exportByTask :
              exportByClient
            }
            data-testid={`export-tab-${tab}`}
            className="text-xs text-forest hover:text-coral inline-flex items-center gap-1"
          >
            <Download size={12} /> Export this view
          </button>
        </div>
      </div>

      {tab === "project" && (
        <div className="mt-6 bg-white rounded-xl border border-cream p-5">
          <div className="font-semibold">Hours by project</div>
          {byProject.length === 0 ? (
            <p className="text-sm text-[#6a736f] mt-4">No hours tracked in this period.</p>
          ) : (
            <div className="mt-4 space-y-3">
              {byProject.map((p) => (
                <div key={p.project_id}>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full" style={{ background: p.client?.color }} />
                      <span>{p.name} <span className="text-[#6a736f]">— {p.client?.name}</span></span>
                    </div>
                    <div className="tabular-nums text-[#6a736f]">{p.hours.toFixed(2)}h · ${p.revenue.toLocaleString()}</div>
                  </div>
                  <div className="mt-1.5 h-2 bg-[#EAF3EE] rounded-full overflow-hidden">
                    <div className="h-full" style={{ width: `${(p.hours / maxHours) * 100}%`, background: p.client?.color || "#22C55E" }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "task" && (
        <div className="mt-6 bg-white rounded-xl border border-cream overflow-hidden" data-testid="by-task-full">
          <table className="w-full text-sm">
            <thead className="bg-cream-2/50 text-xs text-[#6a736f]">
              <tr className="text-left">
                <th className="px-4 py-3">Task</th>
                <th className="px-4 py-3 text-right">Hours</th>
                <th className="px-4 py-3 text-right">Billable hrs</th>
                <th className="px-4 py-3 text-right">Revenue</th>
                <th className="px-4 py-3 text-right">Cost</th>
                <th className="px-4 py-3 text-right">Margin</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EAF3EE]">
              {byTask.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-sm text-[#6a736f]">Log time against tasks to see the breakdown here.</td></tr>
              ) : byTask.map((t) => {
                const margin = (t.revenue || 0) - (t.cost || 0);
                return (
                  <tr key={t.task_id || t.task_name} data-testid={`by-task-full-row-${t.task_id || t.task_name}`}>
                    <td className="px-4 py-3">
                      {t.task_name}
                      {!t.task_id && <span className="ml-2 text-[10px] bg-cream text-[#6a736f] px-1 rounded">unclassified</span>}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{t.hours.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{t.billable_hours.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">${t.revenue.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right tabular-nums">${t.cost.toLocaleString()}</td>
                    <td className={`px-4 py-3 text-right tabular-nums font-medium ${margin >= 0 ? "text-forest" : "text-red-600"}`}>
                      ${margin.toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {tab === "client" && (
        <div className="mt-6 bg-white rounded-xl border border-cream overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-cream-2/50 text-xs text-[#6a736f]">
              <tr className="text-left">
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Hours</th>
                <th className="px-4 py-3 text-right">Revenue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EAF3EE]">
              {byClient.length === 0 ? (
                <tr><td colSpan={3} className="px-4 py-6 text-center text-sm text-[#6a736f]">No clients yet.</td></tr>
              ) : byClient.map((c) => (
                <tr key={c.id}>
                  <td className="px-4 py-3 flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: c.color }} />{c.name}</td>
                  <td className="px-4 py-3 tabular-nums">{c.hours.toFixed(2)}h</td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium">${c.revenue.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, accent, sub }) {
  return (
    <div className="bg-white rounded-xl border border-cream p-5">
      <div className="text-xs text-[#6a736f]">{label}</div>
      <div className={`font-display text-3xl mt-1 ${accent ? "text-coral" : ""}`}>{value}</div>
      {sub && <div className="text-[11px] text-[#6a736f] mt-1">{sub}</div>}
    </div>
  );
}
